import { memo, type FC } from "react";

export type FinishedDeckSectionPaintIds = {
  aluminiumBaseGradient: string;
  aluminiumBrushPattern: string;
  woodBaseGradient: string;
  woodGrainPattern: string;
  woodSubstructureGrainPattern: string;
};

type FinishedDeckSectionDefsProps = {
  ids: FinishedDeckSectionPaintIds;
};

const BOARD_COLOR = "#9b6a3c";

// Per section instance (see FinishedDeckSectionCard's sectionId).
export const getFinishedDeckSectionPaintIds = (sectionId: string): FinishedDeckSectionPaintIds => ({
  aluminiumBaseGradient: `${sectionId}-finished-section-aluminium-base`,
  aluminiumBrushPattern: `${sectionId}-finished-section-aluminium-brush`,
  woodBaseGradient: `${sectionId}-finished-section-wood-base`,
  woodGrainPattern: `${sectionId}-finished-section-wood-grain`,
  woodSubstructureGrainPattern: `${sectionId}-finished-section-substructure-grain`,
});

// The section's static paints - wood and aluminium gradients and textures - rendered inside its <defs>.
const FinishedDeckSectionDefs: FC<FinishedDeckSectionDefsProps> = ({ ids }) => (
  <>
    <linearGradient id={ids.woodBaseGradient} x1="0" x2="0" y1="0" y2="1">
      <stop offset="0%" stopColor="#b77b43" />
      <stop offset="42%" stopColor={BOARD_COLOR} />
      <stop offset="100%" stopColor="#7c4b2b" />
    </linearGradient>
    <linearGradient id={ids.aluminiumBaseGradient} x1="0" x2="0" y1="0" y2="1">
      <stop offset="0%" stopColor="#eef1f2" />
      <stop offset="18%" stopColor="#cdd3d7" />
      <stop offset="48%" stopColor="#cfd3d7" />
      <stop offset="72%" stopColor="#aeb6bc" />
      <stop offset="100%" stopColor="#e3e7e9" />
    </linearGradient>
    <pattern id={ids.woodGrainPattern} width="112" height="34" patternUnits="userSpaceOnUse">
      <path
        d="M-10 8 C16 2 32 14 58 8 S98 7 124 11"
        fill="none"
        stroke="#5b351d"
        strokeOpacity="0.28"
        strokeWidth="1.2"
      />
      <path
        d="M-8 18 C22 24 42 13 70 19 S104 24 126 17"
        fill="none"
        stroke="#d29a62"
        strokeOpacity="0.25"
        strokeWidth="1"
      />
      <path
        d="M-12 28 C18 22 43 31 76 26 S105 22 128 28"
        fill="none"
        stroke="#4d2f1b"
        strokeOpacity="0.22"
        strokeWidth="0.9"
      />
      <ellipse cx="53" cy="16" rx="13" ry="4.5" fill="none" stroke="#6b4024" strokeOpacity="0.22" />
    </pattern>
    <pattern id={ids.woodSubstructureGrainPattern} width="120" height="24" patternUnits="userSpaceOnUse">
      <path
        d="M-8 7 C18 3 40 10 66 6 S102 8 128 4"
        fill="none"
        stroke="#4f311d"
        strokeOpacity="0.25"
        strokeWidth="1.1"
      />
      <path
        d="M-6 15 C21 18 47 12 76 16 S106 20 128 15"
        fill="none"
        stroke="#d19a65"
        strokeOpacity="0.2"
        strokeWidth="1"
      />
      <path d="M0 23 H120" fill="none" stroke="#5f3b23" strokeOpacity="0.2" strokeWidth="0.8" />
    </pattern>
    <pattern id={ids.aluminiumBrushPattern} width="84" height="18" patternUnits="userSpaceOnUse">
      <path d="M0 3 H84" stroke="#ffffff" strokeOpacity="0.45" strokeWidth="0.8" />
      <path d="M0 7 H84" stroke="#7c858c" strokeOpacity="0.2" strokeWidth="0.8" />
      <path d="M0 12 H84" stroke="#ffffff" strokeOpacity="0.28" strokeWidth="0.7" />
      <path d="M0 16 H84" stroke="#6f7880" strokeOpacity="0.18" strokeWidth="0.7" />
      <path d="M6 0 V18 M42 0 V18 M78 0 V18" stroke="#f7f9fa" strokeOpacity="0.12" strokeWidth="1" />
    </pattern>
  </>
);

// Memoized: its ids never change for a mounted section, so it never re-renders.
export default memo(FinishedDeckSectionDefs);
